import { signal } from '@preact/signals';

/** UI-facing multiplayer state (Preact signals), written by the net layer only. */

export type NetStatus = 'offline' | 'connecting' | 'online';
export type ChatChannel = 'zone' | 'party' | 'guild' | 'whisper' | 'system';

export interface ChatLine {
  id: number;
  ch: ChatChannel;
  from: string;
  text: string;
  at: number;
  self: boolean;
  /** Whisper partner (for replies). */
  peer?: string;
}

export interface NearbyPlayer {
  peer: string;
  name: string;
  classId: string;
  level: number;
  ilvl: number;
  party: string | null;
  guild: string | null;
}

export interface PartyMemberInfo {
  uid: string;
  peer: string | null;
  name: string;
  classId: string;
  level: number;
  ilvl: number;
  zone: string | null;
  hpPct: number;
  alive: boolean;
  leader: boolean;
  self: boolean;
}

export interface InviteView {
  id: string;
  kind: 'party' | 'guild' | 'trade' | 'friend';
  from: string;
  fromName: string;
  detail: string;
  expires: number;
}

export interface ReadyCheckView {
  rid: string;
  label: string;
  leaderName: string;
  expires: number;
  mine: boolean | null;
  isLeader: boolean;
  responses: { name: string; ok: boolean | null }[];
}

export interface QueueView {
  content: string;
  label: string;
  since: number;
  count: number;
  size: number;
}

export interface TradeOfferView {
  items: { id: string; count: number }[];
  silver: number;
  locked: boolean;
  confirmed: boolean;
}

export interface TradeView {
  tid: string;
  peer: string;
  peerName: string;
  mine: TradeOfferView;
  theirs: TradeOfferView;
}

export interface FriendView {
  uid: string;
  name: string;
  classId: string;
  online: boolean;
  zone: string | null;
  level: number;
}

export interface GuildMemberView {
  uid: string;
  name: string;
  classId: string;
  level: number;
  zone: string | null;
  online: boolean;
  rank: 'leader' | 'member';
}

export interface GuildView {
  name: string;
  tag: string;
  rank: 'leader' | 'member';
  members: GuildMemberView[];
}

export const netUi = {
  status: signal<NetStatus>('offline'),
  mode: signal<string>('off'),
  selfId: signal(''),
  /** Zone channel number (fields and towns). */
  channel: signal(1),
  /** Other players in the current zone. */
  zonePeers: signal<NearbyPlayer[]>([]),
  isHost: signal(true),
  rtt: signal(0),
  chat: signal<ChatLine[]>([]),
  chatUnread: signal(0),
  party: signal<PartyMemberInfo[]>([]),
  invites: signal<InviteView[]>([]),
  readyCheck: signal<ReadyCheckView | null>(null),
  queue: signal<QueueView | null>(null),
  trade: signal<TradeView | null>(null),
  guild: signal<GuildView | null>(null),
  friends: signal<FriendView[]>([]),
  /** Last connectivity problem, shown in the settings/party panel. */
  warning: signal<string | null>(null),
};

let lineId = 1;
const MAX_LINES = 200;

export function pushChat(line: Omit<ChatLine, 'id' | 'at'> & { at?: number }): void {
  const next = [...netUi.chat.value, { ...line, id: lineId++, at: line.at ?? Date.now() }];
  if (next.length > MAX_LINES) next.splice(0, next.length - MAX_LINES);
  netUi.chat.value = next;
  if (!line.self) netUi.chatUnread.value++;
}

export function systemChat(text: string): void {
  pushChat({ ch: 'system', from: '', text, self: false });
}
